import type React from "react";
import { useEffect, useState } from "react";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { CalendarIcon } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../Select";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Button } from "../Button";
import { Switch } from "../Switch";
import { Label } from "../Label";
import { cn } from "../../../utils/classMerger";
import { format } from "date-fns";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../Accordion";
import { Textarea } from "../Textarea";
import { makeInitialSchedule, type InitialSchedule } from "./types";
import AutopilotUsageNote from "./AutopilotUsageNote";
import type { ContentLength, SocialPlatforms } from "../../campaign/types";
import { CONTENT_LENGTH_OPTIONS } from "../../campaign/utils/contentLengths";
import { getPlatformIcon } from "../../../utils/logo-getter";
import ReconfirmOverlay from "../ReconfirmOverlay";
import ConfirmPopup from "../ConfirmPopup";
import {
    FOOTER_OFF_CANCEL_TEXT,
    FOOTER_OFF_CONFIRM_TEXT,
    FOOTER_OFF_WARNING_MESSAGE,
    FOOTER_OFF_WARNING_MESSAGE_TWO,
    FOOTER_OFF_WARNING_TITLE,
} from "../../../utils/sharedListingFooter";
import CustomDatePicker from "../CutomDatePicker";

export interface PlatformSettingsProps {
    platformSchedules: Record<string, InitialSchedule>;
    setPlatformSchedules: React.Dispatch<
        React.SetStateAction<Record<string, InitialSchedule>>
    >;
    defaultSettings: boolean;
    setDefaultSettings: React.Dispatch<React.SetStateAction<boolean>>;
    hasIndividualChanges: boolean;
    setHasIndividualChanges: React.Dispatch<React.SetStateAction<boolean>>;
    postType?: string; // Add postType to filter platforms
    currentStep: number;
    previousStep: number | null;
    rememberedDefaultSchedule: InitialSchedule | null;
    setRememberedDefaultSchedule: React.Dispatch<React.SetStateAction<InitialSchedule | null>>;
    hasScheduleChangesInStep4: boolean;
    setHasScheduleChangesInStep4: React.Dispatch<React.SetStateAction<boolean>>;
    hideFooter?: boolean;
    /** The listing is marketed for another agent; ask before the footer goes off. */
    sharedListing?: boolean;
}

const PlatformSettings: React.FC<PlatformSettingsProps> = ({
    platformSchedules,
    setPlatformSchedules,
    defaultSettings,
    setDefaultSettings,
    hasIndividualChanges,
    setHasIndividualChanges,
    postType,
    currentStep,
    previousStep,
    rememberedDefaultSchedule,
    setRememberedDefaultSchedule,
    hasScheduleChangesInStep4,
    setHasScheduleChangesInStep4,
    hideFooter = false,
    sharedListing = false,
}) => {
    const isMobile = useIsMobile(900);
    const [calendarOpenPlatform, setCalendarOpenPlatform] = useState<
        string | null
    >(null);

    const [showReconfirmOverlay, setShowReconfirmOverlay] = useState(false);
    const [footerOffPending, setFooterOffPending] = useState<string | null>(null);
    const [footerOffAcknowledged, setFooterOffAcknowledged] = useState(false);
    
    // Navigation effect - handle step transitions
    useEffect(() => {      
        if (currentStep === 3) {
            if (previousStep === 2) {
                // Coming FROM step 2 TO step 3 - first time arriving at schedule settings
                // Always store the current "default" schedule (allow re-capture)
                if (defaultSettings && !rememberedDefaultSchedule) {
                    const firstSchedule = Object.values(platformSchedules)[0];
                    if (firstSchedule) {
                        // Deep clone to avoid reference issues
                        const captured = {
                            ...firstSchedule,
                            date: new Date(firstSchedule.date) // Clone the date object too
                        };
                        setRememberedDefaultSchedule(captured);
                    }
                }
            } else if (previousStep === 4) {
                // Coming FROM step 4 TO step 3 - user is returning after making individual changes
                
                // Show toast if user made schedule changes in Step 4
                if (hasScheduleChangesInStep4) {
                    // Reset the flag after showing toast
                    setHasScheduleChangesInStep4(false);
                }
                
                // Apply remembered default when returning from step 4 to step 3
                // Only if we have a remembered schedule
                if (defaultSettings && rememberedDefaultSchedule) {
                    
                    const updatedSchedules = { ...platformSchedules };
                    Object.keys(updatedSchedules).forEach((key) => {
                        updatedSchedules[key] = {
                            ...updatedSchedules[key],
                            date: new Date(rememberedDefaultSchedule.date),
                            hour: rememberedDefaultSchedule.hour,
                            minute: rememberedDefaultSchedule.minute,
                            period: rememberedDefaultSchedule.period,
                        };
                    });
                    
                    setPlatformSchedules(updatedSchedules);
                }
            }
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

    const handleScheduleChange = (
        platformKey: string | null,
        field: keyof InitialSchedule,
        value: any
    ) => {
        if (defaultSettings) {
            // When using defaults, apply the change to *every* platform
            setPlatformSchedules((prev) => {
                const updated = Object.fromEntries(
                    Object.entries(prev).map(([key, sched]) => [
                        key,
                        { ...sched, [field]: value },
                    ])
                );
                return updated;
            });

            // Update the remembered default schedule when in default mode
            if (rememberedDefaultSchedule) {
                setRememberedDefaultSchedule(prev => prev ? {
                    ...prev,
                    [field]: value,
                } : null);
            }
        } else {
            // Only change this one platform
            setPlatformSchedules((prev) => {
                const updated = {
                    ...prev,
                    [platformKey!]: {
                        ...prev[platformKey!],
                        [field]: value,
                    },
                };
                return updated;
            });
            setHasIndividualChanges(true);
        }
    };

    const handleContentLengthChange = (
        platformKey: string,
        length: ContentLength
    ) => {
        setPlatformSchedules((prev) => ({
            ...prev,
            [platformKey]: {
                ...prev[platformKey],
                contentLength: length,
            },
        }));

        if (!defaultSettings) {
            setHasIndividualChanges(true);
        }
    };

    const toggleBooleanSetting = (
        platformKey: string,
        field: keyof InitialSchedule
    ) => {
        setPlatformSchedules((prev) => ({
            ...prev,
            [platformKey]: {
                ...prev[platformKey],
                [field]: !prev[platformKey][field],
            },
        }));

        if (!defaultSettings) {
            setHasIndividualChanges(true);
        }
    };

    // Switching the footer off on a colleague's listing drops the listing
    // agent's attribution, so the user confirms once per visit first.
    const handleFooterSwitch = (platformKey: string, checked: boolean) => {
        if (!checked && sharedListing && !footerOffAcknowledged) {
            setFooterOffPending(platformKey);
            return;
        }
        toggleBooleanSetting(platformKey, "includeFooter");
    };

    const confirmFooterOff = () => {
        const platformKey = footerOffPending;
        setFooterOffPending(null);
        setFooterOffAcknowledged(true);
        if (platformKey) toggleBooleanSetting(platformKey, "includeFooter");
    };

    const handleFooterChange = (platformKey: string, value: string) => {
        const trimmedValue = value.slice(
            0,
            platformKey === "twitter" ? 280 : 300
        );
        setPlatformSchedules((prev) => ({
            ...prev,
            [platformKey]: {
                ...prev[platformKey],
                footer: trimmedValue,
            },
        }));

        if (!defaultSettings) {
            setHasIndividualChanges(true);
        }
    };

    const handleFooterTypeChange = (
        platformKey: string,
        type: "comment" | "in-post"
    ) => {
        setPlatformSchedules((prev) => ({
            ...prev,
            [platformKey]: {
                ...prev[platformKey],
                footerType: type,
            },
        }));

        if (!defaultSettings) {
            setHasIndividualChanges(true);
        }
    };

    // Get the default schedule for all platforms
    const findBestSchedule = (): InitialSchedule | null => {
        // Use remembered default when:
        // 1. We're in default settings mode
        // 2. User came back from step 4
        // 3. We have a remembered schedule
        if (defaultSettings && previousStep === 4 && rememberedDefaultSchedule) {
            return rememberedDefaultSchedule;
        }

        const schedules = Object.values(platformSchedules);
        if (schedules.length === 0) return null;
        
        const currentTime = new Date();
        
        // First, try to find a platform with a future date
        const futureSchedule = schedules.find(schedule => 
            schedule?.date && schedule.date > currentTime
        );
        
        if (futureSchedule) {
            return futureSchedule;
        }
        
        // Fallback to first schedule if none have future dates
        const result = schedules[0] || null;
        return result;
    };
    
    // Always non-null when defaultSettings is true so the schedule UI renders.
    // Fall back to a freshly-built schedule if findBestSchedule() returns null
    // (e.g. platformSchedules is unexpectedly empty for a given listing flow).
    const defaultSchedule =
        findBestSchedule() ??
        Object.values(platformSchedules)[0] ??
        makeInitialSchedule();
    
    // State tracking: Log current platformSchedules to monitor date/time persistence

    const applyDefaultSettingsToAll = () => {
        // Use the remembered default schedule if available, otherwise use the first platform's schedule
        const scheduleToApply = rememberedDefaultSchedule || Object.values(platformSchedules)[0];

        if (scheduleToApply) {
            // Apply the remembered/default schedule to all platforms
            const updatedSchedules = { ...platformSchedules };

            Object.keys(updatedSchedules).forEach((key) => {
                updatedSchedules[key] = {
                    ...updatedSchedules[key],
                    date: new Date(scheduleToApply.date), // Clone the date
                    hour: scheduleToApply.hour,
                    minute: scheduleToApply.minute,
                    period: scheduleToApply.period,
                };
            });

            setPlatformSchedules(updatedSchedules);
        }

        setDefaultSettings(true);
        setHasIndividualChanges(false);
    };

    useEffect(() => {
        const style = document.createElement("style");
        style.textContent = `
              .custom-time-input::-webkit-calendar-picker-indicator {
                background: none;
                display: none;
              }
              .custom-time-input::-webkit-datetime-edit-fields-wrapper {
                padding: 0;
              }
              .custom-time-input::-webkit-datetime-edit-hour-field,
              .custom-time-input::-webkit-datetime-edit-minute-field,
              .custom-time-input::-webkit-datetime-edit-ampm-field {
                padding: 0 4px;
              }
              .custom-time-input::-webkit-datetime-edit-hour-field:focus,
              .custom-time-input::-webkit-datetime-edit-minute-field:focus,
              .custom-time-input::-webkit-datetime-edit-ampm-field:focus {
                background-color: #E9C468;
                color: #193456;
              }
            `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);
    return (
        <div className="relative h-full w-full overflow-x-hidden space-y-6">
            <AutopilotUsageNote platformSchedules={platformSchedules} />
            {defaultSettings && defaultSchedule && (
                <div className="mt-4">
                    <h3 className="text-primary font-semibold">
                        Post Schedule (All Platforms)
                    </h3>
                    <div
                        className={`flex items-center flex-wrap gap-2 mt-2 ${
                            isMobile ? "flex-col justify-center" : "flex-row"
                        }`}
                    >
                        <h3 className="text-primary font-semibold">Date</h3>
                        <div className={isMobile ? "w-full flex flex-col items-center" : "flex items-center space-x-2"}>
                            <Popover
                                open={calendarOpenPlatform === "default"}
                                onOpenChange={(open) =>
                                    setCalendarOpenPlatform(
                                        open ? "default" : null
                                    )
                                }
                            >
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "justify-start text-left font-normal"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {defaultSchedule.date
                                            ? format(
                                                  defaultSchedule.date,
                                                  "PPP"
                                              )
                                            : "Select date"}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className={isMobile ? "w-auto p-0 max-h-[40vh] overflow-y-auto" : "w-auto p-0"}
                                    align={isMobile ? "center" : "start"}
                                    portalled={false}
                                    collisionPadding={isMobile ? 16 : undefined}
                                    avoidCollisions={true}
                                >
                                    <CustomDatePicker
                                        date={defaultSchedule.date}
                                        setDate={(newDate) => {
                                            // Update date for all platforms
                                            Object.keys(
                                                platformSchedules
                                            ).forEach((key) => {
                                                handleScheduleChange(
                                                    key,
                                                    "date",
                                                    newDate
                                                );
                                            });
                                        }}
                                        restriction="fortnight"
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <h3 className="text-primary font-semibold">Time</h3>

                        <div className="flex items-center space-x-2">
                            <Select
                                value={defaultSchedule.hour?.toString()}
                                onValueChange={(value) => {
                                    // Update hour for all platforms
                                    Object.keys(platformSchedules).forEach(
                                        (key) => {
                                            handleScheduleChange(
                                                key,
                                                "hour",
                                                Number(value)
                                            );
                                        }
                                    );
                                }}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="Hour" />
                                </SelectTrigger>
                                <SelectContent>
                                    {hourOptions.map((hour) => (
                                        <SelectItem
                                            key={`hour-${hour}`}
                                            value={hour.toString()}
                                        >
                                            {hour.toString().padStart(2, "0")}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={defaultSchedule.minute?.toString()}
                                onValueChange={(value) => {
                                    // Update minute for all platforms
                                    Object.keys(platformSchedules).forEach(
                                        (key) => {
                                            handleScheduleChange(
                                                key,
                                                "minute",
                                                Number(value)
                                            );
                                        }
                                    );
                                }}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="Min" />
                                </SelectTrigger>
                                <SelectContent>
                                    {minuteOptions.map((minute) => (
                                        <SelectItem
                                            key={`min-${minute}`}
                                            value={minute.toString()}
                                        >
                                            {minute.toString().padStart(2, "0")}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={defaultSchedule.period}
                                onValueChange={(value) => {
                                    // Update period for all platforms
                                    Object.keys(platformSchedules).forEach(
                                        (key) => {
                                            handleScheduleChange(
                                                key,
                                                "period",
                                                value
                                            );
                                        }
                                    );
                                }}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="AM/PM" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="AM">AM</SelectItem>
                                    <SelectItem value="PM">PM</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            )}
           { postType !== "Lead Generation" &&  <div className="flex flex-col justify-center">
                <h3 className="text-primary font-semibold">
                    Do you want to use default settings?
                </h3>
                <div className="flex items-center space-x-4 mt-2">
                    <Button
                        variant={defaultSettings ? "default" : "outline"}
                        onClick={() => {
                            if (!defaultSettings && hasIndividualChanges) {
                                setShowReconfirmOverlay(true);
                            } else {
                                setDefaultSettings(true);
                            }
                        }}
                    >
                        Yes
                    </Button>
                    <Button
                        variant={!defaultSettings ? "default" : "outline"}
                        onClick={() => setDefaultSettings(false)}
                    >
                        No
                    </Button>
                </div>
            </div>}
            {!defaultSettings && (
                <div className="space-y-4">
                    {Object.entries(platformSchedules)
                        .filter(([platformKey]) => !(postType === 'Lead Generation' && platformKey === 'google'))
                        .map(([platformKey, schedule]) =>
                            schedule && (
                                <Accordion
                                    type="single"
                                    collapsible
                                    className="space-y-4"
                                    key={platformKey}
                                >
                                    <AccordionItem
                                        value={`item-${platformKey}`}
                                        className="bg-white rounded-lg shadow-md"
                                    >
                                        <AccordionTrigger className="px-4 py-3 text-left text-gray-900 hover:text-gray-600 transition-colors duration-200">
                                            <span className="mr-2">
                                                {getPlatformIcon(platformKey)}
                                            </span>
                                            <span className="font-semibold text-primary">
                                                {platformKey === "twitter"
                                                    ? "X (formerly Twitter)"
                                                    : platformKey
                                                          .charAt(0)
                                                          .toUpperCase() +
                                                      platformKey.slice(1)}
                                            </span>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-4 py-3 text-gray-700">
                                            <div className="mt-4">
                                                <h3 className="text-primary font-semibold">
                                                    Post Schedule
                                                </h3>
                                                <div
                                                    className={`flex items-center ${
                                                        isMobile
                                                            ? "flex-col justify-center space-y-4"
                                                            : "flex-row space-x-4"
                                                    } space-x-2 mt-2`}
                                                >
                                                    <h3 className="text-primary font-semibold">
                                                        Date
                                                    </h3>
                                                    <div className="flex items-center space-x-2">
                                                        <Popover
                                                            open={
                                                                calendarOpenPlatform ===
                                                                platformKey
                                                            }
                                                            onOpenChange={(
                                                                open
                                                            ) =>
                                                                setCalendarOpenPlatform(
                                                                    open
                                                                        ? platformKey
                                                                        : null
                                                                )
                                                            }
                                                        >
                                                            <PopoverTrigger
                                                                asChild
                                                            >
                                                                <Button
                                                                    variant="outline"
                                                                    className={cn(
                                                                        "justify-start text-left font-normal"
                                                                    )}
                                                                >
                                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                                    {schedule?.date
                                                                        ? format(
                                                                              schedule.date,
                                                                              "PPP"
                                                                          )
                                                                        : "Select date"}
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent
                                                                className={isMobile ? "w-auto p-0 max-h-[50vh] overflow-y-auto" : "w-auto p-0"}
                                                                align={isMobile ? "center" : "start"}
                                                                portalled={false}
                                                                collisionPadding={isMobile ? 16 : undefined}
                                                                avoidCollisions={true}
                                                            >
                                                                <CustomDatePicker
                                                                    date={
                                                                        schedule.date
                                                                    }
                                                                    setDate={(
                                                                        newDate
                                                                    ) =>
                                                                        handleScheduleChange(
                                                                            platformKey,
                                                                            "date",
                                                                            newDate
                                                                        )
                                                                    }
                                                                    restriction="month"
                                                                />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>
                                                    <h3 className="text-primary font-semibold">
                                                        Time
                                                    </h3>
                                                    <div className="flex items-center space-x-2">
                                                        <Select
                                                            value={schedule.hour?.toString()}
                                                            onValueChange={(
                                                                value
                                                            ) =>
                                                                handleScheduleChange(
                                                                    platformKey,
                                                                    "hour",
                                                                    Number(
                                                                        value
                                                                    )
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger className="w-[70px]">
                                                                <SelectValue placeholder="Hour" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {hourOptions.map(
                                                                    (hour) => (
                                                                        <SelectItem
                                                                            key={`hour-${platformKey}-${hour}`}
                                                                            value={hour.toString()}
                                                                        >
                                                                            {hour
                                                                                .toString()
                                                                                .padStart(
                                                                                    2,
                                                                                    "0"
                                                                                )}
                                                                        </SelectItem>
                                                                    )
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                        <Select
                                                            value={schedule.minute.toString()}
                                                            onValueChange={(
                                                                value
                                                            ) =>
                                                                handleScheduleChange(
                                                                    platformKey,
                                                                    "minute",
                                                                    Number(
                                                                        value
                                                                    )
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger className="w-[70px]">
                                                                <SelectValue placeholder="Min" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {minuteOptions.map(
                                                                    (
                                                                        minute
                                                                    ) => (
                                                                        <SelectItem
                                                                            key={`min-${platformKey}-${minute}`}
                                                                            value={minute.toString()}
                                                                        >
                                                                            {minute
                                                                                .toString()
                                                                                .padStart(
                                                                                    2,
                                                                                    "0"
                                                                                )}
                                                                        </SelectItem>
                                                                    )
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                        <Select
                                                            value={
                                                                schedule.period
                                                            }
                                                            onValueChange={(
                                                                value
                                                            ) =>
                                                                handleScheduleChange(
                                                                    platformKey,
                                                                    "period",
                                                                    value
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger className="w-[70px]">
                                                                <SelectValue placeholder="AM/PM" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="AM">
                                                                    AM
                                                                </SelectItem>
                                                                <SelectItem value="PM">
                                                                    PM
                                                                </SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* AI-only options — hidden in manual flow (My Brand / AI enhance disabled).
                                                Uncomment the wrapping condition to restore when AI is added to these flows. */}
                                            {!hideFooter && (
                                            <div className="mt-4">
                                                <h3 className="text-primary font-semibold">
                                                    Content Length
                                                </h3>
                                                <div className="flex items-center justify-center space-x-2 mt-2">
                                                    {CONTENT_LENGTH_OPTIONS[
                                                        (platformKey === "youtube"
                                                            ? "YouTube"
                                                            : platformKey === "linkedin"
                                                            ? "LinkedIn"
                                                            : platformKey
                                                                  .charAt(0)
                                                                  .toUpperCase() +
                                                              platformKey.slice(
                                                                  1
                                                              )) as SocialPlatforms
                                                    ]?.map(
                                                        (
                                                            option: ContentLength
                                                        ) => (
                                                            <label
                                                                key={option}
                                                                className={`flex items-center justify-center px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                                                                    schedule.contentLength ===
                                                                    option
                                                                        ? "bg-secondary text-white"
                                                                        : "bg-muted hover:bg-lightSecondary"
                                                                }`}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name={`${platformKey}-length`}
                                                                    value={
                                                                        option
                                                                    }
                                                                    checked={
                                                                        schedule.contentLength ===
                                                                        option
                                                                    }
                                                                    onChange={() =>
                                                                        handleContentLengthChange(
                                                                            platformKey,
                                                                            option
                                                                        )
                                                                    }
                                                                    className="sr-only"
                                                                />
                                                                {option}
                                                            </label>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                            )}

                                            <div className="mt-4 space-y-4">
                                                {!hideFooter && (
                                                <div className="flex flex-row justify-between items-center w-full">
                                                    <Label
                                                        htmlFor={`${platformKey}-use-emojis`}
                                                        className="text-primary flex flex-col space-y-1 font-semibold"
                                                    >
                                                        <span>
                                                            Use Emojis in Posts
                                                        </span>
                                                        <span className="text-black font-normal text-sm text-muted-foreground">
                                                            Include emojis in
                                                            your social media
                                                            posts
                                                        </span>
                                                    </Label>
                                                    <Switch
                                                        id={`${platformKey}-use-emojis`}
                                                        checked={
                                                            schedule.emojis ??
                                                            true
                                                        }
                                                        onCheckedChange={() =>
                                                            toggleBooleanSetting(
                                                                platformKey,
                                                                "emojis"
                                                            )
                                                        }
                                                    />
                                                </div>
                                                )}

                                                {!hideFooter && (
                                                <div className="flex flex-row justify-between items-center w-full">
                                                    <Label
                                                        htmlFor={`${platformKey}-use-price`}
                                                        className="text-primary flex flex-col space-y-1 font-semibold"
                                                    >
                                                        <span>
                                                            Use Price in Posts
                                                        </span>
                                                        <span className="text-black font-normal text-sm text-muted-foreground">
                                                            Include price in
                                                            your social media
                                                            posts if available
                                                        </span>
                                                    </Label>
                                                    <Switch
                                                        id={`${platformKey}-use-price`}
                                                        checked={
                                                            schedule.usePrice ??
                                                            true
                                                        }
                                                        onCheckedChange={() =>
                                                            toggleBooleanSetting(
                                                                platformKey,
                                                                "usePrice"
                                                            )
                                                        }
                                                    />
                                                </div>
                                                )}

                                                {platformKey === "youtube" && (
                                                    <>
                                                        <div className="flex flex-row justify-between items-center w-full">
                                                            <Label
                                                                htmlFor={`${platformKey}-youtube-visibility`}
                                                                className="text-primary flex flex-col space-y-1 font-semibold"
                                                            >
                                                                <span>
                                                                    YouTube Video Visibility
                                                                </span>
                                                                <span className="text-black font-normal text-sm text-muted-foreground">
                                                                    Set your YouTube video as Public by default
                                                                </span>
                                                            </Label>
                                                            <Switch
                                                                id={`${platformKey}-youtube-visibility`}
                                                                checked={
                                                                    (schedule.youtubeVisibility ?? "public") === "public"
                                                                }
                                                                onCheckedChange={(checked) =>
                                                                    setPlatformSchedules((prev) => ({
                                                                        ...prev,
                                                                        [platformKey]: {
                                                                            ...prev[platformKey],
                                                                            youtubeVisibility: checked ? "public" : "private",
                                                                        },
                                                                    }))
                                                                }
                                                            />
                                                        </div>

                                                        <div className="flex flex-row justify-between items-center w-full">
                                                            <Label
                                                                htmlFor={`${platformKey}-notify-subscribers`}
                                                                className="text-primary flex flex-col space-y-1 font-semibold"
                                                            >
                                                                <span>
                                                                    Notify Subscribers
                                                                </span>
                                                                <span className="text-black font-normal text-sm text-muted-foreground">
                                                                    Notify your YouTube subscribers when this video is published
                                                                </span>
                                                            </Label>
                                                            <Switch
                                                                id={`${platformKey}-notify-subscribers`}
                                                                checked={
                                                                    schedule.notifySubscribers ?? true
                                                                }
                                                                onCheckedChange={() =>
                                                                    toggleBooleanSetting(
                                                                        platformKey,
                                                                        "notifySubscribers"
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {!hideFooter && <div className="mt-6">
                                                <div className="flex flex-col justify-center items-start">
                                                    <div className="flex flex-col w-full">
                                                        <div className="flex items-center gap-2 mt-2">
                                                            <h4 className="text-sm font-semibold text-primary">
                                                                Include Footer
                                                            </h4>
                                                            <Switch
                                                                checked={
                                                                    schedule.includeFooter
                                                                }
                                                                onCheckedChange={(checked) =>
                                                                    handleFooterSwitch(
                                                                        platformKey,
                                                                        checked
                                                                    )
                                                                }
                                                            />
                                                        </div>

                                                        {schedule.includeFooter && (
                                                            <>
                                                                <span className="font-semibold text-primary mb-1 mt-2 text-sm">
                                                                    Listing
                                                                    Information:
                                                                </span>
                                                                <Textarea
                                                                    value={
                                                                        schedule.footer
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleFooterChange(
                                                                            platformKey,
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    placeholder={
                                                                        platformKey ===
                                                                        "instagram"
                                                                            ? "Enter text to be sent as the first comment for your Instagram post. Please refrain from including the MLS number, phone numbers or property address as there is a high probability that Instagram will flag the comment as spam and it will not be shown to your followers."
                                                                            : `Enter text to be sent as the first comment for your ${platformKey} post.`
                                                                    }
                                                                    className="min-h-[120px]"
                                                                />
                                                                <span
                                                                    className={`text-xs text-muted-foreground mt-1 flex justify-end ${
                                                                        schedule
                                                                            .footer
                                                                            .length >
                                                                        (platformKey ===
                                                                        "twitter"
                                                                            ? 280
                                                                            : 300)
                                                                            ? "text-red-500"
                                                                            : "text-black"
                                                                    }`}
                                                                >
                                                                    {
                                                                        schedule
                                                                            .footer
                                                                            .length
                                                                    }{" "}
                                                                    /{" "}
                                                                    {platformKey ===
                                                                    "twitter"
                                                                        ? "280"
                                                                        : "300"}{" "}
                                                                    characters
                                                                </span>

                                                                {platformKey ===
                                                                    "google" && (
                                                                    <div className="my-2 p-2 border border-red-300 bg-red-50 rounded">
                                                                        <p className="text-xs text-red-600">
                                                                            Note:
                                                                            Adding
                                                                            phone
                                                                            numbers
                                                                            will
                                                                            block
                                                                            the
                                                                            post
                                                                            from
                                                                            being
                                                                            published
                                                                            or
                                                                            may
                                                                            be
                                                                            removed
                                                                            by
                                                                            Google.
                                                                        </p>
                                                                    </div>
                                                                )}

                                                                {/* Footer placement options */}
                                                                {schedule.includeFooter && platformKey !== "google" && (
                                                                    <div
                                                                        className={`flex ${
                                                                            isMobile
                                                                                ? "flex-col justify-center mt-2"
                                                                                : "flex-row items-center"
                                                                        } justify-start mt-4`}
                                                                    >
                                                                        <div
                                                                            className={`flex items-center gap-2`}
                                                                        >
                                                                            <h4 className="text-sm font-semibold text-primary">
                                                                                Footer
                                                                                Placement
                                                                            </h4>
                                                                        </div>
                                                                        <div
                                                                            className={`flex items-center gap-4 ${
                                                                                isMobile
                                                                                    ? "mt-2"
                                                                                    : "ml-4"
                                                                            }`}
                                                                        >
                                                                            {platformKey !==
                                                                                "pinterest" &&
                                                                                platformKey !==
                                                                                    "google" && (
                                                                                    <label
                                                                                        className={`flex items-center gap-1 text-sm ${
                                                                                            schedule.footerType ===
                                                                                            "comment"
                                                                                                ? "text-primary"
                                                                                                : "text-muted-foreground"
                                                                                        }`}
                                                                                    >
                                                                                        <input
                                                                                            type="radio"
                                                                                            id={`${platformKey}-comment-radio`}
                                                                                            name={`${platformKey}-footerType`}
                                                                                            value="comment"
                                                                                            checked={
                                                                                                schedule.footerType ===
                                                                                                "comment"
                                                                                            }
                                                                                            onChange={() =>
                                                                                                handleFooterTypeChange(
                                                                                                    platformKey,
                                                                                                    "comment"
                                                                                                )
                                                                                            }
                                                                                            disabled={
                                                                                                platformKey ===
                                                                                                "pinterest"
                                                                                            }
                                                                                        />
                                                                                        As
                                                                                        comment
                                                                                    </label>
                                                                                )}

                                                                            {platformKey !==
                                                                                "instagram" &&
                                                                                platformKey !==
                                                                                    "twitter" && (
                                                                                    <label
                                                                                        className={`flex items-center gap-1 text-sm ${
                                                                                            schedule.footerType ===
                                                                                            "in-post"
                                                                                                ? "text-primary"
                                                                                                : "text-muted-foreground"
                                                                                        }`}
                                                                                    >
                                                                                        <input
                                                                                            type="radio"
                                                                                            id={`${platformKey}-inpost-radio`}
                                                                                            name={`${platformKey}-footerType`}
                                                                                            value="in-post"
                                                                                            checked={
                                                                                                schedule.footerType ===
                                                                                                "in-post"
                                                                                            }
                                                                                            onChange={() =>
                                                                                                handleFooterTypeChange(
                                                                                                    platformKey,
                                                                                                    "in-post"
                                                                                                )
                                                                                            }
                                                                                            disabled={
                                                                                                platformKey ===
                                                                                                "instagram"
                                                                                            }
                                                                                        />
                                                                                        In
                                                                                        post
                                                                                    </label>
                                                                                )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>}
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            )
                    )}
                </div>
            )}

            <ReconfirmOverlay
                isOpen={showReconfirmOverlay}
                onClose={() => setShowReconfirmOverlay(false)}
                onConfirm={() => {
                    applyDefaultSettingsToAll();
                    setShowReconfirmOverlay(false);
                }}
                title="Apply Default Settings"
                message="This will remove all individual platform changes."
                messageTwo="Are you sure you want to continue?"
                confirmText="Apply Default Settings"
            />

            <ConfirmPopup
                open={footerOffPending !== null}
                title={FOOTER_OFF_WARNING_TITLE}
                message={FOOTER_OFF_WARNING_MESSAGE}
                messageTwo={FOOTER_OFF_WARNING_MESSAGE_TWO}
                confirmText={FOOTER_OFF_CONFIRM_TEXT}
                cancelText={FOOTER_OFF_CANCEL_TEXT}
                onConfirm={confirmFooterOff}
                onCancel={() => setFooterOffPending(null)}
            />
        </div>
    );
};

export default PlatformSettings;
